import { Box, styled, Tooltip } from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { CSSProperties, useEffect, useMemo, useState } from 'react';

import { type AircraftEvent } from '#entities';
import {
  EAircraftEventType,
  EAircraftStatus,
  EFlightCategory,
  ENOPOrganType,
  ESeverity,
  getOpacityHex,
  type EMaoMaintenanceBadge,
} from '#shared';

import { useData } from '../../data-context';
import { getAirportLabel, getTimeSeverityFromCrewDuty } from '../../helpers';

import { useAircraftDrawer } from './AircraftDrawerContext';

const PREFIX = 'TSMED-AircraftDrawerTimeline';

export const aircraftDrawerTimelineClasses = {
  curTimeMarker: `${PREFIX}-curTimeMarker`,
  curTimeMarkerValue: `${PREFIX}-curTimeMarkerValue`,
  dayMarker: `${PREFIX}-dayMarker`,
  dayMarkerCur: `${PREFIX}-dayMarkerPrev`,
  dayMarkerNext: `${PREFIX}-dayMarkerNext`,
  section: `${PREFIX}-section`,
  sectionHeader: `${PREFIX}-sectionHeader`,
  eventsSection: `${PREFIX}-eventsSection`,
  crewDutiesSection: `${PREFIX}-crewDutiesSection`,
  airportsSection: `${PREFIX}-airportsSection`,
  sectionContent: `${PREFIX}-sectionContent`,
  scheduleGroups: `${PREFIX}-scheduleGroups`,
  scheduleGroup: `${PREFIX}-scheduleGroup`,
  scheduleList: `${PREFIX}-scheduleList`,
  scheduleItem: `${PREFIX}-scheduleItem`,
  scheduleLocationItem: `${PREFIX}-scheduleLocationItem`,
  scheduleLabel: `${PREFIX}-scheduleLabel`,
  scheduleTime: `${PREFIX}-scheduleTime`,
};

const Root = styled(Box)(
  ({ theme: { spacing, palette, mixins, utils, breakpoints } }) => ({
    color: 'white',

    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    gap: spacing(2),
    paddingBlock: spacing(4),
    [`.${aircraftDrawerTimelineClasses.curTimeMarker}`]: {
      position: 'absolute',
      width: 2,
      background: 'rgba(255,255,255,0.3)',
      zIndex: 2,
      height: '100%',
      mixBlendMode: 'difference',
      backgroundImage: 'linear-gradient(black 6px, transparent 0)',
      backgroundSize: '4px 16px',
      backgroundPosition: '0px 4px',
      top: 0,
      [`.${aircraftDrawerTimelineClasses.curTimeMarkerValue}`]: {
        position: 'absolute',
        fontSize: utils.clamp(14, 24),
        whiteSpace: 'nowrap',
        left: 4,
      },
    },
    [`.${aircraftDrawerTimelineClasses.dayMarker}`]: {
      position: 'absolute',
      width: 2,
      background: 'rgba(255,255,255,0.3)',
      zIndex: 1,
      height: 'calc(100% - 32px)',
      bottom: 0,
      mixBlendMode: 'difference',
      backgroundImage: 'linear-gradient(black 6px, transparent 0)',
      backgroundSize: '4px 16px',
      backgroundPosition: '0px 4px',
      [`.${aircraftDrawerTimelineClasses.dayMarkerCur},.${aircraftDrawerTimelineClasses.dayMarkerNext}`]:
        {
          position: 'absolute',
          bottom: 0,
          fontSize: utils.clamp(10, 12),
          whiteSpace: 'nowrap',
        },
      [`.${aircraftDrawerTimelineClasses.dayMarkerCur}`]: {
        right: 8,
      },
      [`.${aircraftDrawerTimelineClasses.dayMarkerNext}`]: {
        left: 8,
      },
    },
    [`.${aircraftDrawerTimelineClasses.section}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),

      [`.${aircraftDrawerTimelineClasses.sectionHeader}`]: {
        fontSize: utils.clamp(10, 16),
        textAlign: 'right',
      },
      [`.${aircraftDrawerTimelineClasses.sectionContent}`]: {},
    },
    [`.${aircraftDrawerTimelineClasses.scheduleGroups}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),
    },
    [`.${aircraftDrawerTimelineClasses.scheduleGroup}`]: {
      background: palette.blue[900],
      position: 'relative',
      width: '100%',
      ['&:before']: {
        zIndex: 1,
        display: 'block',
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: spacing(2),
        background:
          'linear-gradient(to left, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
      },
      ['&:after']: {
        zIndex: 1,
        display: 'block',
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: spacing(2),
        background:
          'linear-gradient(to right, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
      },
    },
    [`.${aircraftDrawerTimelineClasses.eventsSection}`]: {
      color: 'black',
      [`.${aircraftDrawerTimelineClasses.scheduleItem}`]: {
        backgroundImage: `linear-gradient(45deg, var(--primaryColor) 25%, var(--secondaryColor) 25%, var(--secondaryColor) 50%, var(--primaryColor) 50%, var(--primaryColor) 75%, var(--secondaryColor) 75%, var(--secondaryColor) 100%)`,
        backgroundSize: spacing(3, 3),
        ['--primaryColor']: `var(--organSecondaryColor, var(--aircraftStatusMaintenanceSecondaryColor,  ${palette.grey[100]}))`,
        ['--secondaryColor']: `var(--organSecondaryColor, var(--aircraftStatusMaintenanceSecondaryColor,  ${palette.grey[100]}${getOpacityHex(0.65)}))`,

        color: 'black',
      },
    },
    [`.${aircraftDrawerTimelineClasses.scheduleList}`]: {
      display: 'flex',
      height: spacing(4.5),
      position: 'relative',
      overflow: 'hidden',
      width: '100%',

      [breakpoints.down('xl')]: {
        height: spacing(3.5),
      },
    },
    [`.${aircraftDrawerTimelineClasses.scheduleItem}`]: {
      flexWrap: 'nowrap',
      display: 'flex',
      position: 'absolute',
      fontSize: utils.clamp(10, 16),
      justifyContent: 'space-between',
      lineHeight: spacing(3.5),
      padding: spacing(0.5, 1),
      borderInline: `2px solid black`,
      whiteSpace: 'nowrap',
      gap: spacing(1),
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out',
      background: 'var(--secondaryColor)',
      color: 'var(--primaryColor)',

      [breakpoints.down('xl')]: {
        lineHeight: spacing(2.5),
      },

      [`.${aircraftDrawerTimelineClasses.scheduleLabel}`]: {
        zIndex: 3,
      },
      [`.${aircraftDrawerTimelineClasses.scheduleTime}`]: {
        zIndex: 3,
      },
      [`&.${aircraftDrawerTimelineClasses.scheduleLocationItem}`]: {
        cursor: 'pointer',
        padding: 0,
        backgroundImage: 'none',
        background: 'black',
        width: 'auto',
        maxWidth: 'none',
        overflow: 'visible',

        [`.${aircraftDrawerTimelineClasses.scheduleLabel}`]: {
          zIndex: 'initial',
          color: 'var(--flightCategoryColor)',
          background: 'var(--flightCategorySecondaryColor)',
          padding: spacing(0.5, 1),
        },
      },

      ...mixins.organStyles(palette, { prefix: '&', secondaryOpacity: 0.6 }),
      ...mixins.flightCategoryStyles(palette, { secondaryOpacity: 0.3 }),
      ...mixins.severityStyles(palette),

      [`&.event-type-${EAircraftEventType.Maintenance}`]: {
        color: 'var(--aircraftStatusMaintenanceColor)',
        background: 'var(--aircraftStatusMaintenanceSecondaryColor)',
        // [`&.maintenance-type-${EMaoMaintenanceBadge.AOG}`]: {
        //   background: 'var(--aircraftStatusMaintenanceColor)',
        //   color: `black`,
        // },
      },
      [`&.event-type-${EAircraftEventType.TripLeg}`]: {
        background: `var(--primaryColor)`,
        color: 'black',
        zIndex: 2,
        borderColor: '#0006',
        ...mixins.organStyles(palette, { prefix: `&` }),
        [`.${aircraftDrawerTimelineClasses.scheduleLabel}`]: {
          zIndex: 4,
          opacity: 0,
        },
        [`.${aircraftDrawerTimelineClasses.scheduleTime}`]: {
          opacity: 0,
        },
      },
    },

    // [`.status-${EAircraftStatus.NotAvailable}`]: {
    //   color: palette.grey[800],
    //   background: `${palette.grey[800]}1A`,
    // },

    [`.status-${EAircraftStatus.OnTrip}`]: {},
  }),
);

interface TimelineItem {
  id: number;
  startUnix: number;
  endUnix: number;
  label: string;
  timeSeverity: ESeverity;
  width: number;
  offset: number;
}

interface AircraftEventTimelineItem extends Omit<
  TimelineItem,
  'timeSeverity' | 'id'
> {
  id: string;
  organ: ENOPOrganType | null;
  maintenanceType: EMaoMaintenanceBadge | null;
  type: AircraftEvent['type'];
}

export interface AircraftDrawerTimelineProps {
  showAircraftEvents?: boolean;
  showCrewDutiesEvents?: boolean;
  showLocations?: boolean;
  showHeaders?: boolean;
  showTimeMarker?: boolean;
  showDateMarker?: boolean;
}

export const AircraftDrawerTimeline = ({
  showAircraftEvents = true,
  showCrewDutiesEvents = true,
  showLocations = true,
  showHeaders = false,
  showTimeMarker = true,
  showDateMarker = true,
}: AircraftDrawerTimelineProps) => {
  const { metarsData } = useData();
  const {
    aircraftData: { CrewDuties, aircraftEvents, airports, airportLastKnown },
    selectAirport,
  } = useAircraftDrawer();
  const currentTimezone = airportLastKnown?.IanaTimeZone;
  const [dayPosition, setDayPosition] = useState(0);
  const crewDutyTimelineGroups = useMemo(
    () =>
      CrewDuties.reduce<{ cursor: number; items: TimelineItem[] }[]>(
        (groups, cd) => {
          const start = cd.OnDutyDateTimeUnix;
          const end = cd.OffDutyDateTimeUnix;
          const name = [
            cd.Crew.Contact.FirstNames.split(' ')
              .map(n => n[0] && `${n[0]}.`)
              .filter(Boolean)
              .join(''),
            cd.Crew.Contact.LastName,
          ].join(' ');
          const timeSeverity = getTimeSeverityFromCrewDuty(cd, currentTimezone);

          const width = ((end - start) / 1000 / 60 / 60 / 24) * 100;
          const offset =
            ((start - new Date().getTime()) / 1000 / 60 / 60 / 24) * 100;
          if (width + offset < 0 || offset > 100) {
            return groups;
          }
          const timelineItem: TimelineItem = {
            id: cd.Id,
            startUnix: start,
            endUnix: end,
            label: name,
            timeSeverity,
            width,
            offset,
          };
          for (const group of groups) {
            if (start > group.cursor) {
              group.items.push(timelineItem);
              group.cursor = end;
              return groups;
            }
          }
          groups.push({ cursor: end, items: [timelineItem] });
          return groups;
        },
        [],
      ),
    [CrewDuties, currentTimezone],
  );

  const aircraftEventTimelineGroups = useMemo(
    () =>
      aircraftEvents.reduce<
        { cursor: number; items: AircraftEventTimelineItem[] }[]
      >((groups, e) => {
        const [startUnix, endUnix] = e.range;

        const width = ((endUnix - startUnix) / 1000 / 60 / 60 / 24) * 100;
        const offset =
          ((startUnix - new Date().getTime()) / 1000 / 60 / 60 / 24) * 100;
        if (width + offset < 0 || offset > 100) {
          return groups;
        }
        const timelineItem: AircraftEventTimelineItem = {
          id: e.id,
          startUnix,
          endUnix,
          label: e.label,
          width,
          offset,
          type: e.type,
          organ: e.organ,
          maintenanceType: e.maintenanceType?.OpsBoardBadge ?? null,
        };
        const timelineItemChildren: AircraftEventTimelineItem[] =
          e.children?.map(child => {
            const startUnix = child.range[0];
            const endUnix = child.range[1];
            const width = ((endUnix - startUnix) / 1000 / 60 / 60 / 24) * 100;
            const offset =
              ((startUnix - new Date().getTime()) / 1000 / 60 / 60 / 24) * 100;
            return {
              id: child.id,
              startUnix,
              endUnix,
              label: child.label,
              width,
              offset,
              type: child.type,
              organ: child.organ,
              maintenanceType: child.maintenanceType?.OpsBoardBadge ?? null,
            };
          }) || [];
        for (const group of groups) {
          if (startUnix > group.cursor) {
            group.items.push(timelineItem);
            group.items.push(...timelineItemChildren);
            group.cursor = endUnix;
            return groups;
          }
        }
        groups.push({
          cursor: endUnix,
          items: [timelineItem, ...timelineItemChildren],
        });
        return groups;
      }, []),
    [aircraftEvents],
  );

  const locationsTimelineGroups = useMemo(() => {
    const result = airports
      .map(({ startUnix, icao, iata, id, airport }) => {
        const offset =
          ((startUnix - new Date().getTime()) / 1000 / 60 / 60 / 24) * 100;
        const flightCategory =
          (icao && metarsData[icao]?.flightCategory) ?? EFlightCategory.NA;
        return {
          id: `${id}-${startUnix}`,
          offset,
          icao,
          label: getAirportLabel(iata, icao),
          flightCategory,
          IanaTimeZone: airport.IanaTimeZone,
        };
      })
      .map((item, index, items) => {
        if (!items[index + 1] || items[index + 1]?.offset > 8) {
          return { ...item, offset: Math.max(0, item.offset) };
        }
        return item;
      });
    if (result.length === 0 && airportLastKnown) {
      const { IATA, ICAO, Id, IanaTimeZone } = airportLastKnown;
      return [
        {
          id: `${Id}-${dayjs().toISOString()}`,
          label: getAirportLabel(IATA, ICAO),
          ICAO,
          offset: 0,
          IanaTimeZone,
        },
      ];
    }
    return result;
  }, [airportLastKnown, airports, metarsData]);
  useEffect(() => {
    const updatePosition = () => {
      setDayPosition(
        (dayjs()
          .tz(currentTimezone)
          .endOf('day')
          .diff(dayjs().tz(currentTimezone), 'minutes') /
          60 /
          24) *
          100,
      );
    };
    const interval = setInterval(updatePosition, 60000);
    updatePosition();
    return () => clearInterval(interval);
  });

  return (
    <Root>
      <>
        {showTimeMarker ? (
          <Box className={aircraftDrawerTimelineClasses.curTimeMarker}>
            <Box className={aircraftDrawerTimelineClasses.curTimeMarkerValue}>
              {dayjs().tz(currentTimezone).format('HH:mm z')}
            </Box>
          </Box>
        ) : null}
        {showDateMarker ? (
          <Box
            className={aircraftDrawerTimelineClasses.dayMarker}
            style={{ left: `${dayPosition}%` } as CSSProperties}
          >
            <Box className={aircraftDrawerTimelineClasses.dayMarkerCur}>
              {dayjs().tz(currentTimezone).format('MMM DD')}
            </Box>
            <Box className={aircraftDrawerTimelineClasses.dayMarkerNext}>
              {dayjs().tz(currentTimezone).add(1, 'day').format('MMM DD')}
            </Box>
          </Box>
        ) : null}
        {showAircraftEvents ? (
          <Box
            className={classNames(
              aircraftDrawerTimelineClasses.section,
              aircraftDrawerTimelineClasses.eventsSection,
            )}
          >
            {showHeaders ? (
              <Box className={aircraftDrawerTimelineClasses.sectionHeader}>
                Aircraft Schedule
              </Box>
            ) : null}
            <Box className={aircraftDrawerTimelineClasses.sectionContent}>
              <Box
                className={classNames(
                  aircraftDrawerTimelineClasses.scheduleGroups,
                )}
              >
                {aircraftEventTimelineGroups.map((group, index) => (
                  <Box
                    key={`${index}-${group.cursor}`}
                    className={classNames(
                      aircraftDrawerTimelineClasses.scheduleGroup,
                    )}
                  >
                    <Box
                      className={classNames(
                        aircraftDrawerTimelineClasses.scheduleList,
                      )}
                    >
                      {group.items.map(event => (
                        <Tooltip
                          key={event.id}
                          title={`${event.label} - ${event.endUnix ? dayjs(event.endUnix).tz(currentTimezone).format('HH:mm z') : ''}`}
                          followCursor
                        >
                          <Box
                            className={classNames(
                              aircraftDrawerTimelineClasses.scheduleItem,
                              `event-type-${event.type}`,

                              {
                                [`aircraft-status-${EAircraftStatus.Maintenance}`]:
                                  event.type === EAircraftEventType.Maintenance,
                                [`organ-type-${event.organ}`]: event.organ,
                                [`maintenance-type-${event.maintenanceType}`]:
                                  event.maintenanceType,
                              },
                            )}
                            style={
                              {
                                left: `${event.offset}%`,
                                width: `${event.width}%`,
                                maxWidth: `${event.width}%`,
                              } as CSSProperties
                            }
                          >
                            <Box
                              className={classNames(
                                aircraftDrawerTimelineClasses.scheduleLabel,
                              )}
                            >
                              {event.label}
                            </Box>
                            <Box
                              className={classNames(
                                aircraftDrawerTimelineClasses.scheduleTime,
                              )}
                            >
                              {event.endUnix
                                ? dayjs(event.endUnix)
                                    .tz(currentTimezone)
                                    .format('HH:mm z')
                                : ''}
                            </Box>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
        {showCrewDutiesEvents ? (
          <Box
            className={classNames(
              aircraftDrawerTimelineClasses.section,
              aircraftDrawerTimelineClasses.crewDutiesSection,
            )}
          >
            {showHeaders ? (
              <Box className={aircraftDrawerTimelineClasses.sectionHeader}>
                Crew Schedule
              </Box>
            ) : null}
            <Box className={aircraftDrawerTimelineClasses.sectionContent}>
              <Box
                className={classNames(
                  aircraftDrawerTimelineClasses.scheduleGroups,
                )}
              >
                {crewDutyTimelineGroups.map((group, index) => (
                  <Box
                    key={`${index}-${group.cursor}`}
                    className={classNames(
                      aircraftDrawerTimelineClasses.scheduleGroup,
                    )}
                  >
                    <Box
                      className={classNames(
                        aircraftDrawerTimelineClasses.scheduleList,
                      )}
                    >
                      {group.items.map(cd => (
                        <Tooltip
                          key={cd.id}
                          title={`${cd.label} - ${dayjs(cd.endUnix).tz(currentTimezone).format('HH:mm z')}`}
                          followCursor
                        >
                          <Box
                            key={cd.id}
                            className={classNames(
                              aircraftDrawerTimelineClasses.scheduleItem,
                              `severity-status-${cd.timeSeverity}`,
                            )}
                            style={
                              {
                                left: `${cd.offset}%`,
                                width: `${cd.width}%`,
                              } as CSSProperties
                            }
                          >
                            <Box
                              className={
                                aircraftDrawerTimelineClasses.scheduleLabel
                              }
                            >
                              {cd.label}
                            </Box>
                            <Box
                              className={
                                aircraftDrawerTimelineClasses.scheduleTime
                              }
                            >
                              {dayjs(cd.endUnix)
                                .tz(currentTimezone)
                                .format('HH:mm z')}
                            </Box>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
        {showLocations ? (
          <Box
            className={classNames(
              aircraftDrawerTimelineClasses.section,
              aircraftDrawerTimelineClasses.airportsSection,
            )}
          >
            {showHeaders ? (
              <Box className={aircraftDrawerTimelineClasses.sectionHeader}>
                Airports
              </Box>
            ) : null}
            <Box className={aircraftDrawerTimelineClasses.sectionContent}>
              <Box
                className={classNames(
                  aircraftDrawerTimelineClasses.scheduleGroups,
                )}
              >
                <Box
                  className={classNames(
                    aircraftDrawerTimelineClasses.scheduleGroup,
                  )}
                >
                  <Box
                    className={classNames(
                      aircraftDrawerTimelineClasses.scheduleList,
                    )}
                  >
                    {locationsTimelineGroups.map(l => (
                      <Tooltip key={l.id} title={l.label} arrow>
                        <Box
                          key={l.id}
                          className={classNames(
                            aircraftDrawerTimelineClasses.scheduleItem,
                            aircraftDrawerTimelineClasses.scheduleLocationItem,
                          )}
                          style={{ left: `${l.offset}%` } as CSSProperties}
                          onClick={() => {
                            selectAirport({
                              Id: l.id,
                              ICAO: l.icao,
                              IanaTimeZone: l.IanaTimeZone,
                            });
                          }}
                        >
                          <Box
                            className={classNames(
                              aircraftDrawerTimelineClasses.scheduleLabel,
                              `flight-category-${l.flightCategory ?? 'NA'}`,
                            )}
                          >
                            {l.label}
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
      </>
    </Root>
  );
};
